import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { PaymentReceiptTypeCreateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputNumber } from "../../../common/controls";
import { InputReference } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { CurrencyCreate } from "../../../currency/components/create/CurrencyCreate";
import { CurrencyCreateStore } from "../../../currency/store";
import { FormControl } from "@mui/material";

export type PaymentReceiptTypeCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  name_esSelectedValue?: { displayId: string; id: string } | null;
  onName_esChange?: (value: string) => void;
  name_es: string;
  name_esError?: string;
  name_enSelectedValue?: { displayId: string; id: string } | null;
  onName_enChange?: (value: string) => void;
  name_en: string;
  name_enError?: string;
  name_ptSelectedValue?: { displayId: string; id: string } | null;
  onName_ptChange?: (value: string) => void;
  name_pt: string;
  name_ptError?: string;
  priceSelectedValue?: { displayId: string; id: string } | null;
  onPriceChange?: (value: number) => void;
  price: number;
  priceError?: string;
  currencySelectedValue?: { displayId: string; id: string } | null;
  onCurrencySelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onCurrencyChange?: (value: string) => void;
  currencyOptions?: { displayId: string; id: string }[];
  currencyError?: string;
  paymentReceiptTypeCreateStore: PaymentReceiptTypeCreateStore;
};

export const PaymentReceiptTypeCreateForm: React.FC<PaymentReceiptTypeCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name_es = "",
      name_esError,
      onName_esChange = () => {},
      name_en = "",
      name_enError,
      onName_enChange = () => {},
      name_pt = "",
      name_ptError,
      onName_ptChange = () => {},
      price = 0,
      priceError,
      onPriceChange = () => {},
      onCurrencySelected = () => {},
      currencySelectedValue = null,
      currencyOptions = [],
      currencyError,
      onCurrencyChange = () => {},
      paymentReceiptTypeCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="PaymentReceiptType"
              mode="create"
              values={{}}
              options={{ currency: currencyOptions }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name_es":
                    onName_esChange?.(valor);
                    break;
                  case "name_en":
                    onName_enChange?.(valor);
                    break;
                  case "name_pt":
                    onName_ptChange?.(valor);
                    break;
                  case "price":
                    onPriceChange?.(valor);
                    break;
                  case "currency":
                    onCurrencySelected?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={paymentReceiptTypeCreateStore.getCardErrors("null")}
              title={langStore.groupPaymentReceiptTypeLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name_es}
                  error={Boolean(name_esError)}
                  warning={
                    !name_esError &&
                    Boolean(paymentReceiptTypeCreateStore.warnings["name_es"])
                  }
                  helperText={
                    name_esError ||
                    paymentReceiptTypeCreateStore.warnings["name_es"]?.message
                  }
                  label={langStore.PaymentReceiptTypeNameLabel + " (es)"}
                  onChange={(value: string) => onName_esChange(value)}
                  description={langStore.PaymentReceiptTypeNameDescription}
                />

                <InputString
                  value={name_en}
                  error={Boolean(name_enError)}
                  warning={
                    !name_enError &&
                    Boolean(paymentReceiptTypeCreateStore.warnings["name_en"])
                  }
                  helperText={
                    name_enError ||
                    paymentReceiptTypeCreateStore.warnings["name_en"]?.message
                  }
                  label={langStore.PaymentReceiptTypeNameLabel + " (en)"}
                  onChange={(value: string) => onName_enChange(value)}
                  description={langStore.PaymentReceiptTypeNameDescription}
                />

                <InputString
                  value={name_pt}
                  error={Boolean(name_ptError)}
                  warning={
                    !name_ptError &&
                    Boolean(paymentReceiptTypeCreateStore.warnings["name_pt"])
                  }
                  helperText={
                    name_ptError ||
                    paymentReceiptTypeCreateStore.warnings["name_pt"]?.message
                  }
                  label={langStore.PaymentReceiptTypeNameLabel + " (pt)"}
                  onChange={(value: string) => onName_ptChange(value)}
                  description={langStore.PaymentReceiptTypeNameDescription}
                />
                <InputNumber
                  value={price}
                  error={Boolean(priceError)}
                  warning={
                    !priceError &&
                    Boolean(paymentReceiptTypeCreateStore.warnings["price"])
                  }
                  helperText={
                    priceError ||
                    paymentReceiptTypeCreateStore.warnings["price"]?.message
                  }
                  allowNegative={true}
                  label={langStore.PaymentReceiptTypePriceLabel}
                  decimalScale={2}
                  description={langStore.PaymentReceiptTypePriceDescription}
                  onChange={(value: number) => onPriceChange(value)}
                />
                <InputReference
                  id="currency"
                  label={langStore.PaymentReceiptTypeCurrencyLabel}
                  error={Boolean(currencyError)}
                  helperText={
                    currencyError ||
                    paymentReceiptTypeCreateStore.warnings["currency"]?.message
                  }
                  selectedValue={currencySelectedValue}
                  onChange={(value: string) => onCurrencyChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onCurrencySelected(value)
                  }
                  options={currencyOptions}
                  description={langStore.PaymentReceiptTypeCurrencyDescription}
                  canCreate={rootStore.authStore.hasPermissions(
                    "currency_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new CurrencyCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <CurrencyCreate
                          currencyCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewCurrency"
                      cancelMethod="cancelNewCurrency"
                    />
                  )}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
